import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDebounceFn } from 'ahooks'
import dayjs from 'dayjs'
import { useUserAtom } from '@/store'
import { getCookieAuthInfo, removeCookieAuthInfo, setCookieAuthInfo } from '@/utils'

export interface IHistory {
  searchKey: string
  searchTime: number
  id: number
}

export const useLocalStorageHistory = () => {
  const { user } = useUserAtom()
  const storageKey = useMemo(() => (user?.id ? `history_${user?.id}` : 'history_guest'), [user?.id])

  const [history, setHistory] = useState<IHistory[]>([])

  useEffect(() => {
    const cookieSearchHistory = getCookieAuthInfo(storageKey)
    const storageData = cookieSearchHistory[storageKey]

    setHistory(storageData ? (JSON.parse(storageData) as IHistory[]) : [])
  }, [storageKey])

  const { run: setHistoryStorage } = useDebounceFn(
    (val: string) => {
      if (!val.trim()) return

      setHistory((prev) => {
        const existingIndex = prev.findIndex((item) => item.searchKey === val)
        let newList = [...prev]

        if (existingIndex !== -1) {
          // 如果已存在：提升到最前面并更新时间
          const item = { ...newList[existingIndex], searchTime: dayjs().unix() }
          newList.splice(existingIndex, 1)
          newList.unshift(item)
        } else {
          // 如果不存在：新增
          const newItem: IHistory = {
            searchKey: val,
            searchTime: dayjs().unix(),
            id: Date.now(),
          }
          newList = [newItem, ...prev].slice(0, 5)
        }
        setCookieAuthInfo(storageKey, JSON.stringify(newList))
        return newList
      })
    },
    { wait: 300 },
  )

  const deleteHistory = useCallback(
    (delKey: string) => {
      setHistory((prev) => {
        const newList = prev.filter((item) => item.searchKey !== delKey)
        if (newList.length === 0) {
          removeCookieAuthInfo(storageKey)
        } else {
          setCookieAuthInfo(storageKey, JSON.stringify(newList))
        }
        return newList
      })
    },
    [storageKey],
  )

  const deleteAllHistory = useCallback(() => {
    removeCookieAuthInfo(storageKey)
    setHistory([])
  }, [storageKey])

  // 3. 使用 useMemo 处理排序，避免渲染时重复计算
  const sortedHistory = useMemo(() => {
    return [...history].sort((a, b) => b.searchTime - a.searchTime)
  }, [history])

  return {
    setHistoryStorage,
    deleteHistory,
    deleteAllHistory,
    history: sortedHistory,
  }
}
